import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toastr } from "react-redux-toastr";
import {
	FiX,
	FiChevronRight,
	FiChevronLeft,
	FiPaperclip,
	FiTrash2,
	FiPlus,
	FiCalendar,
} from "react-icons/fi";
import moment from "moment";
import Moment from "react-moment";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { expensesService, expenseGroupingsService, expStatus } from "services";
import MobileAttachmentThumb from "./MobileAttachmentThumb";
import * as expensesActions from "actions/expensesActions";
import { errorFormating } from "utils/utils";
import "./MobileExpenseNewSheet.scss";

// ─── Helpers de fecha ─────────────────────────────────────────────────────────

const fmtDate = (str) => {
	if (!str) return null;
	const [y, m, d] = str.split("-");
	return `${d}/${m}/${y}`;
};

const strToDate = (str) => {
	if (!str) return null;
	return new Date(`${str}T00:00:00`);
};

const dateToStr = (d) => {
	if (!d) return null;
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
};

// ─── Hook: altura real del viewport ──────────────────────────────────────────

function useViewportHeight(enabled) {
	const [height, setHeight] = useState(null);
	useEffect(() => {
		if (!enabled) {
			setHeight(null);
			return undefined;
		}
		const vv = window.visualViewport;
		if (!vv) return undefined;
		const handler = () => setHeight(vv.height);
		vv.addEventListener("resize", handler);
		handler();
		return () => vv.removeEventListener("resize", handler);
	}, [enabled]);
	return height;
}

// ─── Sub-view: lista genérica (categoría / proveedor) ────────────────────────

const ListSubView = ({ title, items, value, onChange, onBack, noneLabel }) => {
	const { t } = useTranslation();
	const [search, setSearch] = useState("");

	const filtered = useMemo(
		() =>
			(items ?? []).filter((item) =>
				item.name.toLowerCase().includes(search.toLowerCase())
			),
		[items, search]
	);

	return (
		<div className="mens-subview">
			<div className="mens-subview__header">
				<button
					type="button"
					className="mens-subview__back"
					onClick={onBack}
					aria-label={t("common.back")}
				>
					<FiChevronLeft size={22} />
				</button>
				<span className="mens-subview__title">{title}</span>
			</div>

			<div className="mens-subview__search-wrap">
				<input
					className="mens-subview__search"
					placeholder={t("expenses.mobile.filters.search", { title: title.toLowerCase() })}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					autoFocus
				/>
			</div>

			<div className="mens-subview__list">
				{noneLabel && (
					<button
						type="button"
						className={`mens-subview__item${!value ? " mens-subview__item--selected" : ""}`}
						onClick={() => {
							onChange("");
							onBack();
						}}
					>
						<span>{noneLabel}</span>
						{!value && (
							<span className="mens-subview__check" aria-hidden>
								✓
							</span>
						)}
					</button>
				)}

				{filtered.map((item) => {
					const isSelected = String(value) === String(item.id);
					return (
						<button
							key={item.id}
							type="button"
							className={`mens-subview__item${isSelected ? " mens-subview__item--selected" : ""}`}
							onClick={() => {
								onChange(String(item.id));
								onBack();
							}}
						>
							<span>{item.name}</span>
							{isSelected && (
								<span className="mens-subview__check" aria-hidden>
									✓
								</span>
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
};

// ─── Sub-view: agrupación con opción de crear ─────────────────────────────────

const GroupingSubView = ({ items, value, onSelect, onBack, onCreateGrouping }) => {
	const { t } = useTranslation();
	const [search, setSearch] = useState("");
	const [creating, setCreating] = useState(false);

	const filtered = useMemo(
		() =>
			(items ?? []).filter((item) =>
				item.name.toLowerCase().includes(search.toLowerCase())
			),
		[items, search]
	);

	const exactMatch = useMemo(
		() =>
			(items ?? []).some(
				(item) => item.name.toLowerCase() === search.trim().toLowerCase()
			),
		[items, search]
	);

	const showCreate = search.trim().length > 0 && !exactMatch;

	const handleCreate = async () => {
		if (!search.trim() || creating) return;
		setCreating(true);
		try {
			await onCreateGrouping(search.trim());
			onBack();
		} catch {
			// error manejado en onCreateGrouping
		} finally {
			setCreating(false);
		}
	};

	return (
		<div className="mens-subview">
			<div className="mens-subview__header">
				<button
					type="button"
					className="mens-subview__back"
					onClick={onBack}
					aria-label={t("common.back")}
				>
					<FiChevronLeft size={22} />
				</button>
				<span className="mens-subview__title">{t("expenses.mobile.form.grouping")}</span>
			</div>

			<div className="mens-subview__search-wrap">
				<input
					className="mens-subview__search"
					placeholder={t("expenses.mobile.form.groupingSearchPlaceholder")}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					autoFocus
				/>
			</div>

			<div className="mens-subview__list">
				<button
					type="button"
					className={`mens-subview__item${!value ? " mens-subview__item--selected" : ""}`}
					onClick={() => {
						onSelect(null, null);
						onBack();
					}}
				>
					<span>{t("expenses.mobile.form.noGrouping")}</span>
					{!value && (
						<span className="mens-subview__check" aria-hidden>
							✓
						</span>
					)}
				</button>

				{showCreate && (
					<button
						type="button"
						className="mens-subview__item mens-subview__item--create"
						onClick={handleCreate}
						disabled={creating}
					>
						<FiPlus size={16} />
						<span>
							{creating
								? t("expenses.mobile.form.creating")
								: t("expenses.mobile.form.createGrouping", { name: search.trim() })}
						</span>
					</button>
				)}

				{filtered.map((item) => {
					const isSelected = value === item.id;
					return (
						<button
							key={item.id}
							type="button"
							className={`mens-subview__item${isSelected ? " mens-subview__item--selected" : ""}`}
							onClick={() => {
								onSelect(item.id, { value: item.id, label: item.name });
								onBack();
							}}
						>
							<span>{item.name}</span>
							{isSelected && (
								<span className="mens-subview__check" aria-hidden>
									✓
								</span>
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
};

// ─── Sub-view: calendario de fecha única ─────────────────────────────────────

const DateSubView = ({ value, onChange, onBack }) => {
	const { t } = useTranslation();
	const selected = strToDate(value);

	return (
		<div className="mens-subview mens-subview--date">
			<div className="mens-subview__header">
				<button
					type="button"
					className="mens-subview__back"
					onClick={onBack}
					aria-label={t("common.back")}
				>
					<FiChevronLeft size={22} />
				</button>
				<span className="mens-subview__title">{t("expenses.mobile.form.selectDay")}</span>
				{value && (
					<span className="mens-subview__date-chip">
						{fmtDate(value)}
					</span>
				)}
			</div>

			<div className="mens-subview__calendar-wrap">
				<DatePicker
					selected={selected}
					onChange={(d) => {
						onChange(d ? dateToStr(d) : null);
						onBack();
					}}
					inline
					calendarClassName="mens-calendar"
					maxDate={new Date()}
				/>
			</div>
		</div>
	);
};

// ─── Componente principal ─────────────────────────────────────────────────────

const EMPTY_FORM = {
	day: "",
	value: "",
	type_id: "",
	payment_type_id: "",
	provider_id: "",
	grouping_id: null,
	comments: "",
	status: expStatus.pending,
};

const MobileExpenseNewSheet = ({
	open = true,
	fullPage = false,
	onClose,
	expense,
	disabled,
	onSaved,
	expense_types,
	expense_groupings,
	providers,
	payment_types,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const isActive = fullPage || open;
	const viewportHeight = useViewportHeight(isActive && !fullPage);

	const [activeView, setActiveView] = useState("main");
	const [form, setForm] = useState(EMPTY_FORM);
	const [selectedGrouping, setSelectedGrouping] = useState(null);
	const [typeMaxValue, setTypeMaxValue] = useState(null);
	const [files, setFiles] = useState([]);
	const [errors, setErrors] = useState({});
	const [isLoading, setIsLoading] = useState(false);

	const isEdit = !!expense?.id;
	const isDisabled = disabled ?? false;

	useEffect(() => {
		if (!isActive) return;
		setActiveView("main");
		setErrors({});
		setFiles([]);

		if (expense) {
			const f = {
				...expense,
				day: expense.day ?? "",
				value: expense.value ?? "",
				provider_id: expense.provider_id ?? expense.provider?.id ?? "",
				type_id: expense.type_id ?? expense.type?.id ?? "",
				payment_type_id:
					expense.payment_type_id ?? expense.payment_type?.id ?? "",
				grouping_id: expense.grouping_id ?? expense.grouping?.id ?? null,
				comments: expense.comments ?? "",
			};
			setForm(f);

			const groupingId = f.grouping_id;
			if (groupingId) {
				const g = expense_groupings?.find((gr) => gr.id === groupingId);
				setSelectedGrouping({
					value: groupingId,
					label: g?.name ?? expense.grouping?.name ?? String(groupingId),
				});
			} else {
				setSelectedGrouping(null);
			}

			const found = expense_types?.find(
				(t) => String(t.id) === String(f.type_id)
			);
			setTypeMaxValue(found?.max_value ?? null);
		} else {
			setForm({ ...EMPTY_FORM, day: moment().format("YYYY-MM-DD") });
			setSelectedGrouping(null);
			setTypeMaxValue(null);
		}
	}, [isActive, expense?.id]); // eslint-disable-line

	useEffect(() => {
		if (!isActive || fullPage) return undefined;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, [isActive, fullPage]);

	// ── Handlers de formulario ─────────────────────────────────────────────────

	const setField = (key, val) => {
		setForm((f) => ({ ...f, [key]: val }));
		if (key === "type_id") {
			const found = expense_types?.find(
				(t) => String(t.id) === String(val)
			);
			setTypeMaxValue(found?.max_value ?? null);
		}
	};

	const handleGroupingSelect = (id, opt) => {
		setSelectedGrouping(opt);
		setForm((f) => ({ ...f, grouping_id: id ?? null }));
	};

	const handleGroupingCreate = async (name) => {
		const response = await expenseGroupingsService.add({
			name,
			enabled: true,
		});
		if (response.ok) {
			await dispatch(expensesActions.getAllExpenseGroupings());
			const newOpt = { value: response.id, label: name };
			setSelectedGrouping(newOpt);
			setForm((f) => ({ ...f, grouping_id: response.id }));
		} else {
			toastr.error(t("common.error"), t("expenses.mobile.form.errorCreateGrouping"));
			throw new Error("create failed");
		}
	};

	const handleFileChange = (e) => {
		const selected = Array.from(e.target.files);
		setFiles((prev) => [...prev, ...selected]);
		e.target.value = "";
	};

	const removeFile = (idx) => {
		setFiles((prev) => prev.filter((_, i) => i !== idx));
	};

	const removeAttachment = async (attachment) => {
		const response = await expensesService.removeAttachment(
			form.id,
			attachment.url
		);
		if (response.ok) {
			setForm((f) => ({
				...f,
				attachments: f.attachments.filter((a) => a.url !== attachment.url),
			}));
		} else {
			toastr.error(t("common.error"), t("expenses.mobile.form.errorRemoveAttachment"));
		}
	};

	const getAttachment = async (attachment) => {
		const response = await expensesService.getAttachment(
			form.id,
			attachment.url
		);
		const url = window.URL.createObjectURL(response);
		const a = document.createElement("a");
		document.body.appendChild(a);
		a.href = url;
		a.download = attachment.name;
		a.click();
		a.remove();
		window.URL.revokeObjectURL(url);
	};

	const handleSubmit = async () => {
		setIsLoading(true);
		const { grouping, attachments, ...toSave } = form;

		let response;
		if (isEdit) {
			response = await expensesService.update({ ...toSave });
		} else {
			response = await expensesService.add(toSave);
		}

		if (response.ok) {
			const savedId = response.id ?? toSave.id;
			if (files.length > 0 && savedId) {
				const formData = new FormData();
				files.forEach((f) => formData.append("filename", f));
				await expensesService.updateAttachments(savedId, formData);
			}
			setIsLoading(false);
			toastr.success(t("common.success"), t("common.changesSaved"));
			onSaved?.();
			onClose();
		} else {
			setIsLoading(false);
			setErrors(errorFormating(response));
		}
	};

	// ── Derivados ──────────────────────────────────────────────────────────────

	const categoryName = expense_types?.find(
		(t) => String(t.id) === String(form.type_id)
	)?.name;
	const providerName = providers?.find(
		(p) => String(p.id) === String(form.provider_id)
	)?.name;
	const limitWarning =
		typeMaxValue != null &&
		typeMaxValue !== "" &&
		parseFloat(form.value) > parseFloat(typeMaxValue)
			? t("expenses.editModal.limitWarning", { max: typeMaxValue })
			: null;

	const title = isDisabled
		? t("expenses.viewExpense")
		: isEdit
			? t("expenses.editExpense")
			: t("expenses.newExpense");

	const shellClass = fullPage ? "mens-page" : "mens-sheet";

	const formContent = (
		<div
			className={shellClass}
			role={fullPage ? undefined : "dialog"}
			aria-label={fullPage ? undefined : title}
		>
				{/* ── Vista principal ─────────────────────────────────── */}
				{activeView === "main" && (
					<>
						<div className="mens-header">
							{fullPage ? (
								<button
									type="button"
									className="mens-header__back"
									onClick={onClose}
									aria-label={t("common.back")}
								>
									<FiChevronLeft size={22} />
								</button>
							) : null}
							<h3 className="mens-header__title">{title}</h3>
							{!fullPage ? (
								<button
									type="button"
									className="mens-header__close"
									onClick={onClose}
									aria-label={t("common.close")}
								>
									<FiX size={22} />
								</button>
							) : (
								<span className="mens-header__spacer" aria-hidden />
							)}
						</div>

						<div className="mens-body">
							{/* Línea 1: Día + Importe */}
							<div className="mens-row-pair">
								<div className="mens-field">
									<label className="mens-label">
										{t("expenses.editModal.day")} <span className="mens-required">*</span>
									</label>
									<button
										type="button"
										className={`mens-date-btn${errors.day ? " mens-date-btn--error" : ""}${form.day ? " mens-date-btn--set" : ""}`}
										onClick={() => !isDisabled && setActiveView("date")}
										style={isDisabled ? { cursor: "default" } : undefined}
									>
										<span className="mens-date-btn__value">
											{form.day ? fmtDate(form.day) : t("expenses.mobile.form.datePlaceholder")}
										</span>
										{!isDisabled && (
											<FiCalendar size={16} className="mens-date-btn__icon" />
										)}
									</button>
									{errors.day && (
										<span className="mens-error-msg">{errors.day}</span>
									)}
								</div>
								<div className="mens-field">
									<label className="mens-label">
										{t("expenses.editModal.amount")} <span className="mens-required">*</span>
									</label>
									<div className="mens-input-euro">
										<input
											type="number"
											step="0.01"
											min="0"
											className={`mens-input${errors.value ? " mens-input--error" : ""}`}
											value={form.value}
											onChange={(e) => setField("value", e.target.value)}
											disabled={isDisabled}
											placeholder="0.00"
										/>
										<span className="mens-input-euro__symbol">€</span>
									</div>
									{errors.value && (
										<span className="mens-error-msg">{errors.value}</span>
									)}
									{limitWarning && (
										<span className="mens-warn-msg">{limitWarning}</span>
									)}
								</div>
							</div>

							{/* Línea 2: Categoría */}
							<button
								type="button"
								className={`mens-nav-row${errors.type_id ? " mens-nav-row--error" : ""}`}
								onClick={() => !isDisabled && setActiveView("category")}
								style={isDisabled ? { cursor: "default" } : undefined}
							>
								<span className="mens-nav-row__label">
									{t("expenses.editModal.category")} <span className="mens-required">*</span>
								</span>
								<span
									className={`mens-nav-row__value${form.type_id ? " mens-nav-row__value--set" : ""}`}
								>
									{categoryName || (isDisabled ? "—" : t("expenses.mobile.form.select"))}
								</span>
								{!isDisabled && (
									<FiChevronRight
										size={18}
										className="mens-nav-row__arrow"
									/>
								)}
							</button>
							{errors.type_id && (
								<span className="mens-error-msg mens-error-msg--row">
									{errors.type_id}
								</span>
							)}

							{/* Línea 3: Método de pago */}
							{(!isDisabled || form.payment_type_id) && (
								<div className="mens-pills-section">
									<span className="mens-label">{t("expenses.mobile.form.paymentMethod")}</span>
									<div className="mens-pills">
										{(payment_types ?? []).map((pt) => {
											const isActive =
												String(form.payment_type_id) === String(pt.id);
											if (isDisabled && !isActive) return null;
											return (
												<button
													key={pt.id}
													type="button"
													className={`mens-pill${isActive ? " mens-pill--active" : ""}`}
													onClick={() =>
														!isDisabled &&
														setField(
															"payment_type_id",
															isActive ? "" : String(pt.id)
														)
													}
												>
													{pt.name}
												</button>
											);
										})}
									</div>
								</div>
							)}

							{/* Línea 4: Proveedor */}
							{(!isDisabled || form.provider_id) && (
								<button
									type="button"
									className="mens-nav-row"
									onClick={() => !isDisabled && setActiveView("provider")}
									style={isDisabled ? { cursor: "default" } : undefined}
								>
									<span className="mens-nav-row__label">{t("expenses.editModal.provider")}</span>
									<span
										className={`mens-nav-row__value${form.provider_id ? " mens-nav-row__value--set" : ""}`}
									>
										{providerName || (isDisabled ? "—" : t("expenses.mobile.form.select"))}
									</span>
									{!isDisabled && (
										<FiChevronRight
											size={18}
											className="mens-nav-row__arrow"
										/>
									)}
								</button>
							)}

							{/* Línea 5: Agrupación */}
							{(!isDisabled || selectedGrouping) && (
								<button
									type="button"
									className="mens-nav-row"
									onClick={() => !isDisabled && setActiveView("grouping")}
									style={isDisabled ? { cursor: "default" } : undefined}
								>
									<span className="mens-nav-row__label">{t("expenses.mobile.form.grouping")}</span>
									<span
										className={`mens-nav-row__value${selectedGrouping ? " mens-nav-row__value--set" : ""}`}
									>
										{selectedGrouping?.label ||
											(isDisabled ? "—" : t("expenses.mobile.form.select"))}
									</span>
									{!isDisabled && (
										<FiChevronRight
											size={18}
											className="mens-nav-row__arrow"
										/>
									)}
								</button>
							)}

							{/* Línea 6: Observaciones */}
							{(!isDisabled || form.comments) && (
								<div className="mens-field mens-field--padded">
									<label className="mens-label">{t("expenses.editModal.comments")}</label>
									<textarea
										className="mens-textarea"
										value={form.comments}
										onChange={(e) => setField("comments", e.target.value)}
										disabled={isDisabled}
										placeholder={isDisabled ? "" : t("expenses.mobile.form.observationsPlaceholder")}
										rows={3}
									/>
								</div>
							)}

							{/* Línea 7: Adjuntos */}
							<div className="mens-field mens-field--padded">
								<label className="mens-label">{t("expenses.mobile.form.attachments")}</label>

								{form.attachments?.length > 0 && (
									<ul className="mens-attachments">
										{form.attachments.map((att) => {
											const sizeMb = att.size / 1_000_000;
											const [sizeVal, sizeUnit] =
												sizeMb < 1
													? [att.size / 1000, "Kb"]
													: [sizeMb, "Mb"];
											return (
												<li key={att.url} className="mens-attachment">
													<MobileAttachmentThumb
														expenseId={form.id}
														attachment={att}
														size={48}
														className="mens-attachment__thumb"
													/>
													<div className="mens-attachment__info">
														<button
															type="button"
															className="mens-attachment__name"
															onClick={() => getAttachment(att)}
														>
															{att.name}
														</button>
														<span className="mens-attachment__size">
															{sizeVal.toFixed(2)}&thinsp;{sizeUnit}
														</span>
													</div>
													{!isDisabled && (
														<button
															type="button"
															className="mens-attachment__remove"
															onClick={() => removeAttachment(att)}
															aria-label={t("expenses.mobile.form.removeAttachment")}
														>
															<FiTrash2 size={14} />
														</button>
													)}
												</li>
											);
										})}
									</ul>
								)}

								{files.length > 0 && (
									<ul className="mens-attachments">
										{files.map((file, i) => {
											const sizeMb = file.size / 1_000_000;
											const [sizeVal, sizeUnit] =
												sizeMb < 1
													? [file.size / 1000, "Kb"]
													: [sizeMb, "Mb"];
											const pendingAtt = { name: file.name, url: null };
											return (
												<li
													key={i}
													className="mens-attachment mens-attachment--pending"
												>
													<MobileAttachmentThumb
														expenseId={null}
														attachment={pendingAtt}
														size={48}
														className="mens-attachment__thumb"
													/>
													<div className="mens-attachment__info">
														<span className="mens-attachment__name mens-attachment__name--pending">
															{file.name}
														</span>
														<span className="mens-attachment__size">
															{sizeVal.toFixed(2)}&thinsp;{sizeUnit}
														</span>
													</div>
													<button
														type="button"
														className="mens-attachment__remove"
														onClick={() => removeFile(i)}
														aria-label={t("expenses.mobile.form.removeFile")}
													>
														<FiTrash2 size={14} />
													</button>
												</li>
											);
										})}
									</ul>
								)}

								{!isDisabled && (
									<label className="mens-file-btn">
										<input
											type="file"
											multiple
											className="mens-file-input"
											onChange={handleFileChange}
										/>
										<FiPaperclip size={16} />
										{t("expenses.mobile.form.attachFiles")}
									</label>
								)}

								{isDisabled &&
									!form.attachments?.length && (
										<span className="mens-empty-attachments">
											{t("expenses.mobile.form.noAttachments")}
										</span>
									)}
							</div>

							{/* Información de auditoría (solo en modo ver/editar) */}
							{isEdit && (
								<div className="mens-audit">
									<div className="mens-audit__row">
										<span className="mens-audit__label">{t("common.status")}</span>
										<span
											className={`mens-audit__badge mens-audit__badge--${(form.status ?? "").toLowerCase()}`}
										>
											{t(`expenses.statusLabels.${form.status}`, form.status)}
										</span>
									</div>
									{form.requested && (
										<div className="mens-audit__row">
											<span className="mens-audit__label">
												{t("expenses.editModal.requestedBy")}
											</span>
											<span className="mens-audit__value">
												{form.requester?.name} {form.requester?.surname}
												<span className="mens-audit__date">
													<Moment format="DD/MM/YYYY HH:mm">
														{form.requested}
													</Moment>
												</span>
											</span>
										</div>
									)}
									{form.answerer && (
										<div className="mens-audit__row">
											<span className="mens-audit__label">
												{form.status === expStatus.denied
													? t("expenses.editModal.deniedBy")
													: t("expenses.editModal.approvedBy")}
											</span>
											<span className="mens-audit__value">
												{form.answerer.name} {form.answerer.surname}
												{form.answered && (
													<span className="mens-audit__date">
														<Moment format="DD/MM/YYYY HH:mm">
															{form.answered}
														</Moment>
													</span>
												)}
											</span>
										</div>
									)}
									{form.payer && (
										<div className="mens-audit__row">
											<span className="mens-audit__label">{t("expenses.editModal.paidBy")}</span>
											<span className="mens-audit__value">
												{form.payer.name} {form.payer.surname}
												{form.paid && (
													<span className="mens-audit__date">
														<Moment format="DD/MM/YYYY HH:mm">
															{form.paid}
														</Moment>
													</span>
												)}
											</span>
										</div>
									)}
									{form.answer_comments && (
										<div className="mens-audit__row">
											<span className="mens-audit__label">
												{t("expenses.editModal.supervisorNotes")}
											</span>
											<span className="mens-audit__value">
												{form.answer_comments}
											</span>
										</div>
									)}
									{form.payer_comments && (
										<div className="mens-audit__row">
											<span className="mens-audit__label">
												{t("expenses.editModal.payerNotes")}
											</span>
											<span className="mens-audit__value">
												{form.payer_comments}
											</span>
										</div>
									)}
								</div>
							)}
						</div>

						<div className="mens-footer">
							<button
								type="button"
								className="btn btn-outline-primary"
								onClick={onClose}
							>
								{isDisabled ? t("common.close") : t("common.cancel")}
							</button>
							{!isDisabled && (
								<button
									type="button"
									className="btn btn-primary"
									onClick={handleSubmit}
									disabled={isLoading}
								>
									{isLoading ? t("common.saving") : t("common.save")}
								</button>
							)}
						</div>
					</>
				)}

				{/* ── Sub-vista: categoría ──────────────────────────── */}
				{activeView === "category" && (
					<ListSubView
						title={t("expenses.editModal.category")}
						items={expense_types}
						value={form.type_id}
						onChange={(val) => setField("type_id", val)}
						onBack={() => setActiveView("main")}
						noneLabel={null}
					/>
				)}

				{/* ── Sub-vista: proveedor ──────────────────────────── */}
				{activeView === "provider" && (
					<ListSubView
						title={t("expenses.editModal.provider")}
						items={providers}
						value={form.provider_id}
						onChange={(val) => setField("provider_id", val)}
						onBack={() => setActiveView("main")}
						noneLabel={t("expenses.mobile.form.noProvider")}
					/>
				)}

			{/* ── Sub-vista: agrupación ─────────────────────────── */}
			{activeView === "grouping" && (
				<GroupingSubView
					items={expense_groupings}
					value={form.grouping_id}
					onSelect={handleGroupingSelect}
					onBack={() => setActiveView("main")}
					onCreateGrouping={handleGroupingCreate}
				/>
			)}

			{/* ── Sub-vista: calendario ─────────────────────────── */}
			{activeView === "date" && (
				<DateSubView
					value={form.day}
					onChange={(val) => setField("day", val)}
					onBack={() => setActiveView("main")}
				/>
			)}
		</div>
	);

	if (fullPage) {
		return formContent;
	}

	return (
		<div
			className="mens-backdrop"
			style={viewportHeight != null ? { height: viewportHeight } : undefined}
			role="presentation"
		>
			{formContent}
		</div>
	);
};

export default MobileExpenseNewSheet;
